import React, { useState, useRef, useEffect } from 'react';

const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024; // 8MB

// `refImages` is an array of { name, dataUrl } — reference images (e.g. a client
// logo) the user attached, to be reused/incorporated in the generated image.
async function generateImage(prompt, refImages) {
  const res = await fetch('/generate-image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      prompt,
      images: (refImages || []).map((img) => ({ name: img.name, dataUrl: img.dataUrl })),
    }),
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.error || 'Image generation failed');
  }
  return data.imageUrl;
}

function ImageBot() {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachError, setAttachError] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    setAttachError('');
    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) {
      setAttachError(`You can attach up to ${MAX_ATTACHMENTS} images.`);
      return;
    }

    files.slice(0, room).forEach((file) => {
      if (!file.type.startsWith('image/')) {
        setAttachError('Only image files can be attached.');
        return;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setAttachError('Each image must be under 8MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments((prev) => [...prev, { id: Date.now() + Math.random(), name: file.name, dataUrl: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleGenerate = async () => {
    const trimmed = prompt.trim();
    if (!trimmed || loading) return;

    const userMessage = { role: 'user', text: trimmed, attachments, id: Date.now() };
    const refImages = attachments;
    setMessages((prev) => [...prev, userMessage]);
    setPrompt('');
    setAttachments([]);
    setAttachError('');
    setLoading(true);

    try {
      const imageUrl = await generateImage(trimmed, refImages);
      setMessages((prev) => [...prev, { role: 'bot', imageUrl, id: Date.now() + 1 }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'error', text: err.message, id: Date.now() + 1 }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <>
      {/* Floating launcher button */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close AI image bot' : 'Open AI image bot'}
        style={{
          position: 'fixed',
          bottom: 28,
          right: 28,
          width: 58,
          height: 58,
          borderRadius: '50%',
          background: open ? '#1a1a1a' : '#5B4FCF',
          border: 'none',
          boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 1000,
          transition: 'background 0.15s ease, transform 0.15s ease',
        }}
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        ) : (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2v3" />
            <circle cx="12" cy="6.5" r="1.1" fill="#fff" stroke="none" />
            <rect x="4.5" y="8.5" width="15" height="11" rx="4" />
            <circle cx="9" cy="14" r="1.4" fill="#fff" stroke="none" />
            <circle cx="15" cy="14" r="1.4" fill="#fff" stroke="none" />
            <path d="M9 17.3h6" />
            <path d="M2.5 12.5v3" />
            <path d="M21.5 12.5v3" />
          </svg>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          style={{
            position: 'fixed',
            bottom: 100,
            right: 28,
            width: 'min(1136px, calc(100vw - 56px))',
            height: 760,
            maxHeight: 'calc(100vh - 140px)',
            background: '#fff',
            borderRadius: 18,
            border: '1px solid #ede9e3',
            boxShadow: '0 20px 48px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 999,
          }}
        >
          {/* Header */}
          <div
            style={{
              background: '#1a1a1a',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ fontFamily: 'DM Serif Display', fontSize: 17, color: '#fff' }}>
                ChalkWalk AI
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Image generation studio</div>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px 18px',
              background: '#F7F5F2',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: '#aaa', fontSize: 13, marginTop: 40, padding: '0 12px' }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>✦</div>
                Describe an image and generate it right here — attach a logo or reference photo and it'll be used in the result. Great for quick stall design concepts.
              </div>
            )}

            {messages.map((m) => {
              if (m.role === 'user') {
                return (
                  <div key={m.id} style={{ alignSelf: 'flex-end', maxWidth: 'min(70%, 600px)' }}>
                    {m.attachments && m.attachments.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, marginBottom: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {m.attachments.map((a) => (
                          <img
                            key={a.id}
                            src={a.dataUrl}
                            alt={a.name}
                            title={a.name}
                            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, border: '1px solid #ede9e3' }}
                          />
                        ))}
                      </div>
                    )}
                    <div
                      style={{
                        background: '#5B4FCF',
                        color: '#fff',
                        borderRadius: '12px 12px 2px 12px',
                        padding: '9px 13px',
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}
                    >
                      {m.text}
                    </div>
                  </div>
                );
              }
              if (m.role === 'bot') {
                return (
                  <div key={m.id} style={{ alignSelf: 'flex-start', width: 'min(85%, 900px)' }}>
                    <img
                      src={m.imageUrl}
                      alt="Generated"
                      style={{ width: '100%', borderRadius: 12, border: '1px solid #ede9e3', display: 'block' }}
                    />
                  </div>
                );
              }
              return (
                <div key={m.id} style={{ alignSelf: 'flex-start', maxWidth: 'min(80%, 600px)' }}>
                  <div
                    style={{
                      background: '#fdecec',
                      color: '#b42318',
                      borderRadius: '12px 12px 12px 2px',
                      padding: '9px 13px',
                      fontSize: 12.5,
                      lineHeight: 1.5,
                      border: '1px solid #f6cfcf',
                    }}
                  >
                    {m.text}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div style={{ alignSelf: 'flex-start' }}>
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #ede9e3',
                    borderRadius: '12px 12px 12px 2px',
                    padding: '10px 14px',
                    fontSize: 12.5,
                    color: '#aaa',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ display: 'inline-block', animation: 'spin 1.2s linear infinite' }}>◌</span>
                  Generating…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{ padding: 14, borderTop: '1px solid #ede9e3', background: '#fff' }}>
            {attachments.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {attachments.map((a) => (
                  <div key={a.id} style={{ position: 'relative' }}>
                    <img
                      src={a.dataUrl}
                      alt={a.name}
                      title={a.name}
                      style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, border: '1px solid #ede9e3', display: 'block' }}
                    />
                    <button
                      onClick={() => removeAttachment(a.id)}
                      aria-label={`Remove ${a.name}`}
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: '#1a1a1a',
                        border: '2px solid #fff',
                        color: '#fff',
                        fontSize: 10,
                        lineHeight: '14px',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {attachError && (
              <div style={{ fontSize: 11.5, color: '#b42318', marginBottom: 8 }}>{attachError}</div>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFilesSelected}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={attachments.length >= MAX_ATTACHMENTS}
                title="Attach reference image (e.g. a logo)"
                aria-label="Attach reference image"
                style={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  borderRadius: 10,
                  border: '1px solid #ede9e3',
                  background: '#faf9f7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: attachments.length >= MAX_ATTACHMENTS ? 'default' : 'pointer',
                  opacity: attachments.length >= MAX_ATTACHMENTS ? 0.4 : 1,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 01-7.78-7.78l9.19-9.19a3.67 3.67 0 015.19 5.19l-9.2 9.19a1.83 1.83 0 01-2.6-2.6l8.49-8.48" />
                </svg>
              </button>

              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe the image to generate…"
                rows={1}
                style={{
                  flex: 1,
                  resize: 'none',
                  border: '1px solid #ede9e3',
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  outline: 'none',
                  maxHeight: 90,
                }}
              />
              <button
                onClick={handleGenerate}
                disabled={!prompt.trim() || loading}
                style={{
                  background: !prompt.trim() || loading ? '#e5e1da' : '#1a1a1a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: !prompt.trim() || loading ? 'default' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ImageBot;
